import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { endOfDay, format, sub } from 'date-fns';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';
import { httpClient } from '@/Config/api/httpClient.config';
import { QueryKey } from '@/Config/api/queryKey';
import { LinkTrackingDto } from '@/Domain/api/Analytics/dto/LinkTracking.dto';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { DateRangeState } from '@/Domain/types/DateRange.types';

// Formats a Date as YYYY-MM-DD in the current locale without shifting the timezone
const toISODate = (date?: Date) => (date ? format(date, 'yyyy-MM-dd') : '');

export const useLinkTracking = (externalRange?: DateRangeState) => {
  const queryClient = useQueryClient();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const defaultEnd = endOfDay(new Date());
  const defaultStart = endOfDay(sub(defaultEnd, { days: 6 }));

  const [internalRange, setInternalRange] = useState({
    startDate: defaultStart,
    endDate: defaultEnd,
  });

  // Use external range if provided, otherwise use internal range
  const range = externalRange || internalRange;

  const pageIndex = pagination.pageIndex;
  const sortKey = sorting[0] ? sorting[0].id : undefined;
  const sortDesc = sorting[0] ? sorting[0].desc : undefined;
  const startDate = range.startDate;
  const endDate = range.endDate;

  const { data, isLoading, isError } = useQuery<LinkTrackingDto>({
    queryKey: [
      QueryKey.analytics.linkTracking,
      { start: toISODate(startDate), end: toISODate(endDate), pageIndex, sortKey, sortDesc },
    ],
    placeholderData: keepPreviousData,
    staleTime: 0,
  });

  const { data: widgetSettings } = useQuery<WidgetSettingsDto>({
    queryKey: [QueryKey.widgetSettings],
  });

  const mutation = useMutation<WidgetSettingsDto, unknown, Partial<WidgetSettingsDto>>({
    mutationKey: [QueryKey.widgetSettings],
    mutationFn: async (updatedData) => {
      queryClient.setQueryData([QueryKey.widgetSettings], (prev: WidgetSettingsDto) => ({
        ...prev,
        ...updatedData,
      }));

      return await httpClient
        .patch('/v1/api/widget/configuration', updatedData)
        .then((res) => res.data);
    },
    onError: (_, __, context) => {
      queryClient.setQueryData([QueryKey.widgetSettings], () => context);
    },
  });

  const handleToggleLinkTracking = (check: boolean) =>
    mutation.mutate({ link_tracking_active: check });

  const handleRangeChange = (dateRange: DateRange | undefined) => {
    if (!externalRange && dateRange?.from && dateRange?.to) {
      setInternalRange({
        startDate: dateRange.from,
        endDate: dateRange.to,
      });
    }
  };

  return {
    data,
    isLoading,
    isError,
    handleRangeChange,
    handleToggleLinkTracking,
    range,
    sorting,
    pagination,
    setPagination,
    setSorting,
    isLinkTrackingActive: widgetSettings?.link_tracking_active,
  };
};
